import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { CheckCircle2, ExternalLink, Loader2, Plus, Search, Sparkles, X } from "lucide-react"
import { StepHeader } from "@/components/activation/StepHeader"
import { StepNavigation } from "@/components/activation/StepNavigation"
import { digitsOnly, INPUT_PATTERNS, placeNameOnly } from "@/components/activation/input-sanitizers"
import { primaryActionClasses } from "@/components/activation/styles"
import { INVESTMENT_STRATEGIES } from "@/components/activation/data/strategies"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useActivation } from "@/lib/activation-context"
import { cn } from "@/lib/utils"

const LOCATION_NAME_PATTERN = /^[A-Za-z\s'-]+$/
const TRAVEL_VALUE_PATTERN = /^\d+$/
const POSTCODE_DISTRICT_PATTERN = /^[A-Z]{1,2}\d[A-Z\d]?$/
const MARKET_LIMIT = 3

export function AreaStep() {
  const {
    step1,
    step2,
    step3,
    setStepData,
    generateStepThreeMarketAnalysis,
    saveStep,
    serverError,
  } = useActivation()
  const navigate = useNavigate()
  const initialClusters = step3.areaSuggestions?.clusters || []
  const initialMarketRecommendations = marketRecommendationsFromAnalyses(
    step3.areaMarketAnalysis?.marketAnalyses,
    initialClusters,
  )
  const initialSelectedMarketId = selectedMarketIdFor(
    step3.selectedMarketId,
    initialMarketRecommendations,
    step3.selectedPostcodes,
  )
  const [preferredLocation, setPreferredLocation] = useState(step3.preferredLocation)
  const [maxTravelValue, setMaxTravelValue] = useState(step3.maxTravelValue || "30")
  const [selectedPostcodes, setSelectedPostcodes] = useState(step3.selectedPostcodes)
  const [newPostcode, setNewPostcode] = useState("")
  const [assistantMessage, setAssistantMessage] = useState(
    step3.areaMarketAnalysis?.assistantMessage || "",
  )
  const [marketRecommendations, setMarketRecommendations] = useState(initialMarketRecommendations)
  const [selectedMarketId, setSelectedMarketId] = useState(initialSelectedMarketId)
  const [showRecommendations, setShowRecommendations] = useState(initialMarketRecommendations.length > 0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [suggestionClusters, setSuggestionClusters] = useState(initialClusters)
  const [submitError, setSubmitError] = useState(null)
  const [suggestionError, setSuggestionError] = useState(null)
  const suggestionRequestRef = useRef(0)
  const suggestionsCachedRef = useRef(Boolean(step3.areaSuggestions?.cached))
  const hasUserEditedRef = useRef(false)

  const primaryStrategy = INVESTMENT_STRATEGIES.find((s) => s.id === step2.primaryStrategy)
  const secondaryStrategy = INVESTMENT_STRATEGIES.find((s) => s.id === step2.secondaryStrategy)
  const validPreferredLocation = LOCATION_NAME_PATTERN.test(preferredLocation.trim())
  const validMaxTravelValue = TRAVEL_VALUE_PATTERN.test(maxTravelValue) && Number(maxTravelValue) > 0
  const canGenerate = validPreferredLocation && validMaxTravelValue
  const showGenerateMissingInputHint = !validPreferredLocation || !validMaxTravelValue
  const canContinue = selectedPostcodes.length === MARKET_LIMIT && validPreferredLocation && validMaxTravelValue

  useEffect(() => {
    if (hasUserEditedRef.current) return

    const clusters = step3.areaSuggestions?.clusters || []
    const nextMarketRecommendations = marketRecommendationsFromAnalyses(
      step3.areaMarketAnalysis?.marketAnalyses,
      clusters,
    )
    const nextSelectedMarketId = selectedMarketIdFor(
      step3.selectedMarketId,
      nextMarketRecommendations,
      step3.selectedPostcodes,
    )
    setPreferredLocation(step3.preferredLocation)
    setMaxTravelValue(step3.maxTravelValue || "30")
    setSelectedPostcodes(step3.selectedPostcodes)
    setSuggestionClusters(clusters)
    setAssistantMessage(step3.areaMarketAnalysis?.assistantMessage || "")
    setMarketRecommendations(nextMarketRecommendations)
    setSelectedMarketId(nextSelectedMarketId)
    suggestionsCachedRef.current = Boolean(step3.areaSuggestions?.cached)
    setShowRecommendations(nextMarketRecommendations.length > 0)
  }, [step3])

  const clearGeneratedAreaState = () => {
    hasUserEditedRef.current = true
    suggestionRequestRef.current += 1
    setIsGenerating(false)
    setSuggestionError(null)
    setSelectedPostcodes([])
    setSuggestionClusters([])
    setMarketRecommendations([])
    setSelectedMarketId(null)
    setAssistantMessage("")
    suggestionsCachedRef.current = false
    setShowRecommendations(false)
    setStepData(3, {
      selectedPostcodes: [],
      selectedMarketId: null,
      postcodeRecommendationsMarketId: null,
      areaSuggestions: null,
      areaMarketAnalysis: null,
      areaRecommendations: null,
    })
  }

  const updatePreferredLocation = (value) => {
    setPreferredLocation(placeNameOnly(value))
    clearGeneratedAreaState()
  }

  const updateMaxTravelValue = (value) => {
    setMaxTravelValue(digitsOnly(value))
    clearGeneratedAreaState()
  }

  const addPostcode = (postcode = newPostcode) => {
    hasUserEditedRef.current = true
    const value = normalizePostcodeDistrict(postcode)
    if (!POSTCODE_DISTRICT_PATTERN.test(value)) return
    if (selectedPostcodes.includes(value) || selectedPostcodes.length >= MARKET_LIMIT) return

    const nextPostcodes = [...selectedPostcodes, value]
    setSelectedPostcodes(nextPostcodes)
    setSelectedMarketId(null)
    setStepData(3, { selectedMarketId: null, selectedPostcodes: nextPostcodes })
    setNewPostcode("")
  }

  const removePostcode = (postcode) => {
    hasUserEditedRef.current = true
    const nextPostcodes = selectedPostcodes.filter((p) => p !== postcode)
    if (nextPostcodes.length === selectedPostcodes.length) return

    setSelectedPostcodes(nextPostcodes)
    setSelectedMarketId(null)
    setStepData(3, { selectedMarketId: null, selectedPostcodes: nextPostcodes })
  }

  const areaSuggestionsSnapshot = () => {
    if (!suggestionClusters.length) return step3.areaSuggestions

    return {
      ...step3.areaSuggestions,
      clusters: suggestionClusters,
      cached: suggestionsCachedRef.current,
    }
  }

  const generateSuggestions = async () => {
    if (!canGenerate) return
    const requestId = suggestionRequestRef.current + 1
    suggestionRequestRef.current = requestId
    setIsGenerating(true)
    setSuggestionError(null)
    setSubmitError(null)
    try {
      const payload = await generateStepThreeMarketAnalysis({
        preferredLocation,
        maxTravelValue,
        travelUnit: "miles",
      })
      if (suggestionRequestRef.current !== requestId) return

      const areaSuggestions = payload.areaSuggestions || {}
      const clusters = areaSuggestions.clusters || []
      const markets = marketRecommendationsFromAnalyses(payload.marketAnalyses, clusters)

      setSuggestionClusters(clusters)
      setMarketRecommendations(markets)
      setSelectedMarketId(null)
      setSelectedPostcodes([])
      setAssistantMessage(payload.assistantMessage || "")
      suggestionsCachedRef.current = Boolean(areaSuggestions.cached)
      setShowRecommendations(true)
      setStepData(3, { selectedMarketId: null, selectedPostcodes: [] })
    } catch (error) {
      if (suggestionRequestRef.current !== requestId) return

      setSuggestionError(error.message)
      setShowRecommendations(true)
      setSuggestionClusters([])
      setMarketRecommendations([])
      setSelectedMarketId(null)
      setAssistantMessage("")
    } finally {
      if (suggestionRequestRef.current === requestId) setIsGenerating(false)
    }
  }

  const selectMarket = (marketId) => {
    hasUserEditedRef.current = true
    if (selectedMarketId === marketId) return

    const market = marketRecommendations.find((candidate) => candidate.id === marketId)
    const postcodes = Array.from(new Set(market?.postcodes || [])).slice(0, MARKET_LIMIT)
    if (postcodes.length !== MARKET_LIMIT) return

    setSelectedMarketId(marketId)
    setSelectedPostcodes(postcodes)
    setSuggestionError(null)
    setStepData(3, { selectedMarketId: marketId, selectedPostcodes: postcodes })
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!canContinue) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const areaSuggestions = areaSuggestionsSnapshot()
      const stepData = {
        preferredLocation,
        maxTravelValue,
        travelUnit: "miles",
        selectedPostcodes,
      }
      const preservedStepData = {
        selectedMarketId,
        areaSuggestions,
        areaMarketAnalysis: step3.areaMarketAnalysis,
      }
      setStepData(3, { ...stepData, ...preservedStepData })
      await saveStep(3, stepData, { preserveStepData: preservedStepData })
      navigate("/activation/step/4")
    } catch (error) {
      setSubmitError(error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="mx-auto w-full max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
        <StepHeader
          step={3}
          title="Area Intelligence"
          description="Sentinel researches three demand-ranked market patches. Select one to activate its three postcode districts."
        />

        <div className="mb-12 grid grid-cols-1 gap-12 lg:grid-cols-5">
          <div className="space-y-8 lg:col-span-2">
            <InvestmentProfile
              primaryStrategy={primaryStrategy}
              secondaryStrategy={secondaryStrategy}
              step1={step1}
              preferredLocation={preferredLocation}
              maxTravelValue={maxTravelValue}
            />

            <LocationConstraints
              preferredLocation={preferredLocation}
              maxTravelValue={maxTravelValue}
              onLocationChange={updatePreferredLocation}
              onRadiusChange={updateMaxTravelValue}
            />

            <ShortlistedMarkets
              selectedPostcodes={selectedPostcodes}
              newPostcode={newPostcode}
              onPostcodeInputChange={setNewPostcode}
              onAddPostcode={addPostcode}
              onRemovePostcode={removePostcode}
            />
          </div>

          <AskRobAreaPanel
            canGenerate={canGenerate}
            showGenerateMissingInputHint={showGenerateMissingInputHint}
            isGenerating={isGenerating}
            marketRecommendations={marketRecommendations}
            selectedMarketId={selectedMarketId}
            assistantMessage={assistantMessage}
            showRecommendations={showRecommendations}
            suggestionError={suggestionError}
            onGenerateSuggestions={generateSuggestions}
            onSelectMarket={selectMarket}
          />
        </div>

        <StepNavigation
          stepNumber={3}
          canContinue={canContinue}
          isSubmitting={isSubmitting}
          continueLabel="Lock Area & Continue"
          onBack={() => navigate("/activation/step/2")}
        />
        {submitError || serverError ? (
          <p className="mt-4 text-center text-sm text-destructive">{submitError || serverError}</p>
        ) : null}
        <MarketGenerationDialog open={isGenerating} />
      </div>
    </form>
  )
}

function MarketGenerationDialog({ open }) {
  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-md"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
          <DialogTitle>Generating Market Recommendations</DialogTitle>
          <DialogDescription>
            Rob is researching current demand across valid postcode districts inside your radius.
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

function InvestmentProfile({ primaryStrategy, secondaryStrategy, step1, preferredLocation, maxTravelValue }) {
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
      <h2 className="text-lg font-bold text-foreground">Your Investment Profile</h2>
      <ProfileRow label="Primary Strategy" value={primaryStrategy?.title || "Not set"} />
      {secondaryStrategy ? <ProfileRow label="Secondary Strategy" value={secondaryStrategy.title} /> : null}
      <ProfileRow
        label="Investment Goal"
        value={`£${step1.targetMonthlyCashflowGross || "0"} monthly cashflow in ${step1.timeframeYears || "0"} years`}
      />
      <ProfileRow
        label="Capital Position"
        value={`£${step1.availableCapital || "0"} available + £${step1.jvCapitalAccess || "0"} JV capital`}
      />
      <ProfileRow
        label="Preferred Radius"
        value={`${maxTravelValue || "30"} miles from ${preferredLocation || "base"}`}
      />
    </div>
  )
}

function LocationConstraints({ preferredLocation, maxTravelValue, onLocationChange, onRadiusChange }) {
  const radius = Number(maxTravelValue || 30)

  return (
    <div className="space-y-6">
      <h2 className="text-sm font-bold uppercase tracking-widest text-foreground">Location Constraints</h2>
      <div className="space-y-2">
        <label htmlFor="preferred-location" className="text-sm font-semibold text-foreground">
          Preferred Base Location <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id="preferred-location"
            type="text"
            autoComplete="off"
            placeholder="e.g. Chester, Birmingham, Manchester"
            pattern={INPUT_PATTERNS.placeName}
            value={preferredLocation}
            onChange={(e) => onLocationChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-background py-3 pl-10 pr-4 text-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label htmlFor="max-travel-radius" className="text-sm font-semibold text-foreground">
            Maximum Travel Radius
          </label>
          <span className="text-sm font-bold text-primary">{radius} miles</span>
        </div>
        <input
          id="max-travel-radius"
          type="range"
          min="5"
          max="100"
          step="5"
          value={radius}
          onChange={(e) => onRadiusChange(e.target.value)}
          className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-muted accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>5 miles</span>
          <span>100 miles</span>
        </div>
      </div>
    </div>
  )
}

function ShortlistedMarkets({
  selectedPostcodes,
  newPostcode,
  onPostcodeInputChange,
  onAddPostcode,
  onRemovePostcode,
}) {
  const full = selectedPostcodes.length >= MARKET_LIMIT
  const canAddPostcode = !full && POSTCODE_DISTRICT_PATTERN.test(newPostcode)

  return (
    <div className="space-y-4 border-t border-border pt-6">
      <div>
        <h2 className="mb-1 text-sm font-bold uppercase tracking-widest text-foreground">Locked Postcodes</h2>
        <p className="text-xs text-muted-foreground">
          Select 3 postcode districts to analyse. Choose manually or add Sentinel recommendations.
        </p>
      </div>

      <div className="relative">
        <input
          type="text"
          autoComplete="off"
          placeholder="Add postcode district (e.g. CH1, CH2, LL13)"
          value={newPostcode}
          disabled={full}
          onChange={(e) => onPostcodeInputChange(normalizePostcodeDistrict(e.target.value))}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return
            e.preventDefault()
            onAddPostcode()
          }}
          className="w-full rounded-lg border border-border bg-background py-3 pl-4 pr-12 font-mono text-sm uppercase transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => onAddPostcode()}
          disabled={!canAddPostcode}
          aria-label="Add postcode district"
          className={cn(
            "absolute right-2 top-1/2 rounded-lg p-1.5 transition-colors",
            canAddPostcode
              ? "text-primary hover:bg-primary/10"
              : "cursor-not-allowed text-muted-foreground",
            "-translate-y-1/2",
          )}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="flex min-h-24 flex-col gap-2">
        {selectedPostcodes.map((postcode) => (
          <div
            key={postcode}
            className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 shadow-sm transition-colors hover:border-primary/50"
          >
            <span className="font-mono text-sm font-bold text-foreground">{postcode}</span>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <button
                type="button"
                onClick={() => onRemovePostcode(postcode)}
                aria-label={`Remove ${postcode}`}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        {selectedPostcodes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 py-6 text-center">
            <p className="mb-1 text-sm font-medium text-muted-foreground">No postcode districts selected</p>
            <p className="text-xs italic text-muted-foreground">Add up to 3 postcode districts to continue.</p>
          </div>
        ) : null}
      </div>

      <p
        className={cn(
          "text-right text-xs font-bold uppercase tracking-widest",
          full ? "text-primary" : "text-muted-foreground",
        )}
      >
        {full ? "3 / 3 Postcodes Locked" : `${selectedPostcodes.length} / 3 Postcodes Selected`}
      </p>
      {full ? <p className="text-right text-xs text-muted-foreground">Maximum of 3 postcode districts selected</p> : null}
    </div>
  )
}

function AskRobAreaPanel({
  canGenerate,
  showGenerateMissingInputHint,
  isGenerating,
  marketRecommendations,
  selectedMarketId,
  assistantMessage,
  showRecommendations,
  suggestionError,
  onGenerateSuggestions,
  onSelectMarket,
}) {
  return (
    <div className="lg:col-span-3">
      <div className="flex min-h-[600px] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-muted/10 p-6">
          <div className="flex items-start gap-4">
            <img
              src="/rob_headshot.jpeg"
              alt="Rob"
              className="size-12 shrink-0 rounded-full border-2 border-background object-cover shadow-sm"
            />
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">Ask Rob</p>
              <h2 className="mb-2 text-2xl font-bold text-foreground">Let&apos;s Find Your Market</h2>
              <p className="mb-3 text-sm text-muted-foreground">
                I&apos;ll narrow down credible markets from your goals, strategy and constraints.
              </p>
              <div className="flex w-fit items-center gap-2 rounded-full bg-chart-5/10 px-3 py-1.5 text-xs font-medium text-chart-5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Uses your strategy and investment goals automatically
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto bg-muted/5 p-6">
          <div className="flex max-w-[85%] gap-4">
            <img
              src="/rob_headshot.jpeg"
              alt="Rob"
              className="size-8 shrink-0 rounded-full border border-border object-cover"
            />
            <div className="rounded-lg rounded-tl-sm border border-border bg-card p-4 shadow-sm">
              <p className="text-sm leading-relaxed text-foreground">
                Right - I&apos;ve got enough information to start narrowing locations.
                <br />
                <br />
                Based on your strategy, income goals, capital position, involvement level and travel radius, I&apos;ll
                identify credible markets worth analysing.
              </p>
            </div>
          </div>

          <div className="pl-12">
            <button
              type="button"
              onClick={onGenerateSuggestions}
              disabled={!canGenerate || isGenerating}
              aria-busy={isGenerating}
              className={primaryActionClasses(!canGenerate || isGenerating, "flex items-center gap-2")}
            >
              <Sparkles className={cn("h-4 w-4", isGenerating ? "animate-pulse" : "")} />
              {showRecommendations ? "Regenerate Market Recommendations" : "Generate Market Recommendations"}
            </button>
            {showGenerateMissingInputHint ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Add a base location to let Rob recommend markets.
              </p>
            ) : null}
          </div>

          <RecommendationOutput
            marketRecommendations={marketRecommendations}
            selectedMarketId={selectedMarketId}
            assistantMessage={assistantMessage}
            showRecommendations={showRecommendations}
            suggestionError={suggestionError}
            onSelectMarket={onSelectMarket}
          />
        </div>
      </div>
    </div>
  )
}

function RecommendationOutput({
  marketRecommendations,
  selectedMarketId,
  assistantMessage,
  showRecommendations,
  suggestionError,
  onSelectMarket,
}) {
  if (suggestionError) {
    return <p className="pl-12 text-sm text-destructive">{suggestionError}</p>
  }

  if (!showRecommendations) return null

  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center gap-4 py-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Recommended Markets</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {marketRecommendations.length > 0 ? (
        <div className="space-y-4">
          {marketRecommendations.map((market, index) => (
            <MarketRecommendationCard
              key={market.id}
              market={market}
              index={index}
              selected={market.id === selectedMarketId}
              onSelectMarket={onSelectMarket}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-12 text-center">
          <p className="text-sm text-muted-foreground">
            No postcode district recommendations returned for these constraints.
          </p>
        </div>
      )}

      <SystemInsightCard message={assistantMessage} />
    </div>
  )
}

function MarketRecommendationCard({ market, index, selected, onSelectMarket }) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-5 shadow-sm transition-colors hover:border-primary/50",
        selected ? "border-primary ring-2 ring-primary/15" : "border-border",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Market Patch {index + 1}
          </p>
          <h3 className="text-xl font-bold text-foreground">{market.name || `Market Patch ${index + 1}`}</h3>
          <MarketDetail label="Patch thesis" value={marketReason(market)} />
          <MarketDetail label="Postcode districts" value={postcodeSummaryFor(market)} />
          <MarketDetail label="Strategy fit" value={strategySummaryFor(market)} />
          <MarketDetail label="Demand evidence" value={evidenceSummaryFor(market)} />
          <MarketDetail label="Capital fit" value={capitalFitFor(market)} />
          <MarketDetail label="Watchout" value={watchoutFor(market)} />
          {travelSummaryFor(market) ? <MarketDetail label="Travel" value={travelSummaryFor(market)} /> : null}
          <MarketSources sources={sourcesFor(market)} />
        </div>
        <button
          type="button"
          onClick={() => onSelectMarket(market.id)}
          aria-label={`Select market patch ${index + 1}`}
          className={cn(
            "rounded-lg px-4 py-2 text-sm font-bold transition-colors",
            selected
              ? "bg-primary text-primary-foreground"
              : "bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground",
          )}
        >
          {selected ? "Selected" : "Select this patch"}
        </button>
      </div>
    </div>
  )
}

function MarketSources({ sources }) {
  if (!sources.length) return null

  return (
    <div className="mt-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Sources</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {sources.map((source) => (
          <a
            key={source.url}
            href={source.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            {source.title || source.url}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        ))}
      </div>
    </div>
  )
}

function MarketDetail({ label, value }) {
  if (!value) return null

  return (
    <div className="mt-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm text-muted-foreground">{value}</p>
    </div>
  )
}

function SystemInsightCard({ message }) {
  if (!message) return null

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-5">
      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">System Insight</p>
      <p className="text-sm text-foreground">{message}</p>
    </div>
  )
}

function ProfileRow({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  )
}

function marketRecommendationsFromClusters(clusters) {
  return clusters.slice(0, MARKET_LIMIT).map((cluster, index) => {
    const postcodes = Array.isArray(cluster.postcodes) ? cluster.postcodes : []
    const patch = cluster.marketPatch || {}
    const patchPostcodes = Array.isArray(patch.postcodes) ? patch.postcodes : postcodes

    return {
      ...cluster,
      id: cluster.id || `market-${index + 1}`,
      name: patch.name || cluster.name,
      localFocus: patch.localFocus || cluster.name,
      marketPatch: patch,
      postcodes: patchPostcodes.slice(0, MARKET_LIMIT),
      evidenceSummary: cluster.evidenceSummary || cluster.marketAnalysis?.evidenceSummary,
      sources: cluster.sources || cluster.marketAnalysis?.sources || [],
    }
  })
}

function marketRecommendationsFromAnalyses(analyses, clusters) {
  if (!Array.isArray(analyses) || analyses.length === 0) return marketRecommendationsFromClusters(clusters)

  const clustersById = new Map(clusters.map((cluster) => [cluster.id, cluster]))

  return analyses.slice(0, MARKET_LIMIT).map((analysis, index) => {
    const cluster = clustersById.get(analysis.id) || {}
    const patch = cluster.marketPatch || {}
    const postcodes = Array.isArray(analysis.postcodes) && analysis.postcodes.length
      ? analysis.postcodes
      : (cluster.postcodes || [])

    return {
      ...cluster,
      ...analysis,
      id: analysis.id || cluster.id || `market-${index + 1}`,
      name: analysis.name || patch.name || cluster.name,
      localFocus: patch.localFocus || cluster.name || analysis.name,
      marketPatch: patch,
      postcodes: postcodes.slice(0, MARKET_LIMIT),
      fitLabel: analysis.fitLabel || cluster.fitLabel,
      evidenceSummary: analysis.evidenceSummary || cluster.marketAnalysis?.evidenceSummary,
      sources: analysis.sources || cluster.marketAnalysis?.sources || cluster.sources || [],
      demandSignals: analysis.demandSignals || cluster.demandSignals || [],
      riskFlags: analysis.riskFlags || cluster.riskFlags || [],
    }
  })
}

function selectedMarketIdFor(savedId, markets, selectedPostcodes) {
  const savedMarket = markets.find((market) => market.id === savedId)
  if (savedMarket && marketMatchesPostcodes(savedMarket, selectedPostcodes)) return savedId

  return markets.find((market) => marketMatchesPostcodes(market, selectedPostcodes))?.id || null
}

function marketMatchesPostcodes(market, selectedPostcodes) {
  const marketPostcodes = new Set(market.postcodes || [])
  const currentPostcodes = Array.from(new Set(selectedPostcodes || []))
  if (marketPostcodes.size !== MARKET_LIMIT || currentPostcodes.length !== MARKET_LIMIT) return false

  return currentPostcodes.every((postcode) => marketPostcodes.has(postcode))
}

function marketReason(market) {
  return market.patchThesis
    || market.marketAnalysis?.patchThesis
    || market.marketPatch?.summary
    || market.demandRationale
    || market.rationale
    || market.reviewNote
    || "Worth reviewing for your selected strategy and constraints."
}

function postcodeSummaryFor(market) {
  return market.postcodes?.length ? market.postcodes.join(", ") : null
}

function strategySummaryFor(market) {
  if (market.strategyFit || market.marketAnalysis?.strategyFit) {
    return market.strategyFit || market.marketAnalysis.strategyFit
  }

  const facts = market.facts || {}
  const primary = facts.primaryStrategyName
  const secondary = facts.secondaryStrategyName
  if (primary && secondary) return `${primary}, with ${secondary} as secondary consideration`
  if (primary) return primary

  return market.rationale || "Your selected strategy"
}

function watchoutFor(market) {
  return market.watchout
    || market.marketAnalysis?.watchout
    || market.riskFlags?.[0]
    || market.reviewNote
    || "Micro-location selection still matters here."
}

function capitalFitFor(market) {
  return market.capitalFit || market.marketAnalysis?.capitalFit || null
}

function evidenceSummaryFor(market) {
  return market.evidenceSummary || market.marketAnalysis?.evidenceSummary || null
}

function sourcesFor(market) {
  const sources = market.sources || market.marketAnalysis?.sources || []

  return sources.filter((source) => source?.url)
}

function travelSummaryFor(market) {
  if (market.marketPatch?.travelSummary) return market.marketPatch.travelSummary

  const miles = market.facts?.maxDistanceMiles || market.facts?.nearestDistanceMiles
  if (!miles) return null

  return `Within ${miles} miles of your base location.`
}

function normalizePostcodeDistrict(value) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4)
}
